import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMaintenanceTicketByIdRelayQuery } from '../../hooks/maintenance/query/V2/useMaintenanceTicketsRelay';
import { useMaintenanceTicketTagsRelayQuery } from '../../hooks/maintenance/query/V2/useMaintenanceTicketTagsRelay';
import {
  mapMaintenanceTicketToLegacy,
  mapMaintenanceTagsToLegacy,
} from '../../hooks/maintenance/query/V2/maintenanceMappers';
import {
  addMaintenanceTicketNote,
  addTagsToMaintenanceTicket,
  cancelMaintenanceTicket,
  getTicketResolutionData,
} from '../../api/maintenance';
import { getCurrentPosition } from '../../util/geolocation';
import {
  CANCEL_RESOLUTION_REASONS,
  CLOSE_RESOLUTION_REASONS,
  MECHANIC_RENTAL_REQUIRED_PARTNERS,
} from './constants';
import { useMechanicRentalsLast24h } from '../../hooks/maintenance/query/useMechanicRentalsLast24h';
import MechanicRentalsList from '../../components/Maintenance/MechanicRentalsList';
import { UsageProgressBar } from '../../components/Maintenance/UsageProgressBar';
import { IMode } from '../../ts/interface/mode.interface';
import { ISystem } from '../../ts/interface/system.interface';
import { IAdmin } from '../../ts/interface/admins.interface';
import { ITag, ITicket } from '../../ts/interface/problem.interface';
import {
  getLocaleDateString,
  getLocaleDateTimeString,
  getPriorityName,
  history,
  toTitleCase,
} from '../../utils';
import TicketTimeline from '../../components/Maintenance/TicketTimeline';
import TicketLocationItem from '../../components/Maintenance/TicketLocationItem';
import useSystemAdmins from '../../hooks/system/query/useSystemAdmins';
import { PropertyList } from '../../components/Redesing/property-list';
import { PropertyListItem } from '../../components/Redesing/property-list-item';
import PropertyListItemLink from '../../components/Redesing/property-list-item-link';
import { SeverityPill } from '../../components/Redesing/severity-pill';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import CustomToast from '../../components/Redesing/custom-toast';
import PageContainer from '../../components/Redesing/page-container';
import PageContent from '../../components/Redesing/page-content';
import CardView from '../../components/Redesing/card-view';
import { Stack } from '@mui/system';
import Grid from '@mui/material/Unstable_Grid2';
import Divider from '@mui/material/Divider';
import MovaticCustomModal from '../../components/Modal/MovaticCustomModal';
import Box from '@mui/material/Box';
import BuildIcon from '@mui/icons-material/Build';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import useSmallScreen from '../../hooks/useSmallScreen';
import {
  MovaticFieldGroupSelect,
  MovaticFieldGroupSelectFilter,
  MovaticFieldGroupText,
} from '../../components/Redesing/movatic-field-group';
import MenuItem from '@mui/material/MenuItem';
import { getCredentials } from '../../api/http';
import useOrganization from '../../hooks/use-organization';
import useCurrentSystemInfo from '../../hooks/system/hook/useCurrentSystemInfo';
import ChangeOrganizationToSystem from '../../hooks/system/hook/ChangeOrganizationToSystem';
import TicketImageManager, {
  TicketImageManagerRef,
} from '../../components/Maintenance/TicketImageManager';
import TicketDetailSkeleton from './TicketDetailSkeleton';
import useStation from '../../hooks/stations/query/useStation';
import { LocationStatus } from '../../ts/enums';
// @ts-ignore
import { Map, Marker } from 'react-leaflet';
import L from 'leaflet';
import { GenericMapTileLayer } from '../../components/mapComponent';
import { mapStationMarker } from '../../components/mapMarker';

const TicketDetail = ({
  admin,
  systemLoaded,
}: {
  admin: IAdmin;
  systemLoaded: boolean;
}) => {
  const organizationInfo = useOrganization();

  const { systemId, systemGid } = getCredentials();
  const { ticketId = '' } = useParams<{ ticketId: string }>();
  const {
    data: ticketRaw,
    isLoading,
    refetch,
  } = useMaintenanceTicketByIdRelayQuery(ticketId, {
    enabled: systemLoaded || Boolean(organizationInfo?.id),
  });

  const ticket = useMemo(
    () => (ticketRaw ? mapMaintenanceTicketToLegacy(ticketRaw) : ({} as ITicket)),
    [ticketRaw]
  );

  const { modeInfo: mode, ...system } = useCurrentSystemInfo(
    ticket?.system_info?.id || systemGid
  );

  const { data: admins = [] } = useSystemAdmins(
    {
      enabled: systemLoaded || Boolean(organizationInfo?.id),
    },
    {
      organization_id: organizationInfo?.id,
    }
  );
  const [pastDue, setPastDue] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [note, setNote] = useState('');

  const [cancelReason, setCancelReason] = useState('');
  const [cancelDetails, setCancelDetails] = useState('');
  const [isCanceling, setIsCanceling] = useState(false);
  const cancelImageManagerRef = useRef<TicketImageManagerRef>(null);
  const [resolutionData, setResolutionData] = useState<{
    reason?: string;
    details?: string;
    location?: { lat: number; lng: number };
    resolved_by?: string;
  } | null>(null);

  const [addTagModal, setAddTagModal] = useState(false);
  const [deleteTagModal, setDeleteTagModal] = useState(false);
  const [tagToDelete, setTagToDelete] = useState('');
  const isSmallScreen = useSmallScreen();
  const [value, setValue] = useState([]);

  const { data: stationsData } = useStation(
    { enabled: systemLoaded || Boolean(organizationInfo?.id) },
    {
      status: LocationStatus.active,
      organization_id: organizationInfo?.id || '',
    }
  );

  const stations = useMemo(() => stationsData?.data || [], [stationsData]);

  useEffect(() => {
    if (ticket?.id && (ticket?.completed_on || ticket?.canceled_on)) {
      getTicketResolutionData(ticket.id).then((data) => {
        if (data) setResolutionData(data);
      });
    }
  }, [ticket?.id, ticket?.completed_on, ticket?.canceled_on]);

  const getResolutionReasonLabel = useCallback((reason: string) => {
    const allReasons = [...CLOSE_RESOLUTION_REASONS, ...CANCEL_RESOLUTION_REASONS];
    return allReasons.find((r) => r.value === reason)?.label || reason;
  }, []);

  const resolutionMarkerIcon = useMemo(
    () =>
      L.divIcon({
        html: '<div style="background:#d32f2f;width:20px;height:20px;border-radius:50%;border:3px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3)"></div>',
        iconSize: [20, 20],
        iconAnchor: [10, 10],
        className: 'leaflet-div-icon',
      }),
    []
  );

  useEffect(() => {
    if (systemLoaded || Boolean(organizationInfo?.id)) {
      refetch();
    }
  }, [systemLoaded, organizationInfo?.id, refetch]);

  const { data: tagsDataRaw = [], isLoading: isTagLoading } =
    useMaintenanceTicketTagsRelayQuery(
      {
        systemId: Boolean(organizationInfo.id) ? undefined : systemGid,
        organizationId: organizationInfo?.id || undefined,
      },
      {
        enabled: systemLoaded || Boolean(organizationInfo?.id),
      }
    );

  const tagsData = useMemo(
    () => (tagsDataRaw ? mapMaintenanceTagsToLegacy(tagsDataRaw) : []),
    [tagsDataRaw]
  );

  const tags = useMemo(() => {
    if (!ticket?.tags) {
      return [];
    }

    return ticket?.tags?.map((item: string) => {
      return {
        label: item,
        onClick: (name: string) => {
          setTagToDelete(name);
          setDeleteTagModal(true);
        },
      };
    });
  }, [ticket]);

  const loadedTags = useMemo(
    () =>
      (tagsData || [])
        .filter(
          (item: ITag) =>
            !organizationInfo.id ||
            (!ticket?.tags?.includes(item.name) &&
              ticket?.system_info?.id === item?.system_info?.id)
        )
        .map((item: ITag) => ({
          label: item.name,
          value: item.id,
        })),
    [tagsData, ticket, organizationInfo]
  );

  useEffect(() => {
    if (ticket?.due_on) {
      setPastDue(isPastDue(ticket));
    }
  }, [ticket]);

  const isPastDue = (ticket: ITicket) => {
    return (
      new Date(ticket.due_on).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) &&
      !ticket.completed_on &&
      !ticket.canceled_on
    );
  };

  const getStatusColor = useCallback((ticket: ITicket) => {
    if (ticket.canceled_on) {
      return 'warning';
    }
    if (ticket.completed_on) {
      return 'success';
    }
    if (ticket.assignee) {
      return 'info';
    }
    return 'error';
  }, []);

  const getStatus = useCallback(
    (ticket: ITicket) => {
      const pastDueStatus = pastDue ? ' - Past Due' : '';
      if (ticket.canceled_on) {
        return 'Canceled';
      }
      if (ticket.completed_on) {
        return 'Closed';
      }
      if (ticket.assignee) {
        return `Assigned${pastDueStatus}`;
      }
      return `Open${pastDueStatus}`;
    },
    [pastDue]
  );

  const getOEMContact = () => {
    switch (system.partner) {
      case 1: // cycle safe
        return (window.location.href = 'mailto:support@cyclesafe.com');
      case 2: // on bikeshare
        return (window.location.href = 'mailto:info@onbikeshare.com');
      default:
        return (window.location.href = 'mailto:support@movatic.co');
    }
    // (more partners listed at the bottom of this file for future reference)
  };

  const closeTicket = () => {
    history.push(`/maintenance/close/${ticketId}`);
  };

  const toggleConfirmationModal = () => {
    setShowModal(!showModal);
  };

  const handleCancelTicket = async () => {
    // Validate resolution reason
    if (!cancelReason) {
      CustomToast({
        type: 'error',
        message: 'Please select a cancellation reason.',
      });
      return;
    }

    setIsCanceling(true);

    try {
      // Run GPS capture and image upload in parallel
      const [position, imagesUploaded] = await Promise.all([
        getCurrentPosition(),
        cancelImageManagerRef.current
          ? cancelImageManagerRef.current.uploadImages(ticketRaw?.id)
          : Promise.resolve(true),
      ]);

      if (!imagesUploaded) {
        CustomToast({
          type: 'error',
          message: 'Failed to upload images. Please try again.',
        });
        setIsCanceling(false);
        return;
      }

      const result = await cancelMaintenanceTicket({
        id: ticket?.id || ticketId,
        resolution_reason: cancelReason,
        resolution_details: cancelDetails || undefined,
        resolution_lat: position?.lat,
        resolution_lng: position?.lng,
      });

      if (result) {
        CustomToast({
          type: 'success',
          message: 'Ticket canceled successfully',
        });
        refetch();
        toggleConfirmationModal();
        history.push('/maintenance');
      }
    } catch {
      CustomToast({
        type: 'error',
        message: 'There was an error canceling the ticket. Please try again.',
      });
    } finally {
      setIsCanceling(false);
    }
  };

  const saveNote = () => {
    if (note !== '') {
      const notePayload = JSON.stringify([
        {
          admin_id: admin.email,
          message: note.toString(),
          created_on: new Date().toISOString(),
          type: 'internal',
        },
      ]);
      CustomAsyncToast({
        promise: () =>
          addMaintenanceTicketNote({
            id: ticket?.id,
            note: notePayload,
          }),
        successMessage: () => 'Note saved successfully.',
        loadingMessage: 'Saving note...',
        errorMessage: 'There was an error saving the note. Please try again.',
      }).then(() => {
        refetch();
        setNote('');
      });
    } else {
      CustomToast({
        type: 'error',
        message: 'You must enter a message to save your note',
      });
    }
  };

  const handleNoteChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNote(e.target.value);
  };

  const hardwareContent = (ticket: ITicket) => {
    // TODO: remove this, just put new_hardware section in content()
    if (system.new_hardware) {
      return (
        <>
          <PropertyListItemLink
            others={{ divider: true }}
            label={toTitleCase(ticket.hardware_type_name)}
            text={
              ticket.hardware_number ||
              ticket[ticket.primary_identifier] ||
              ticket.hardware_id
            }
            onClick={() => history.push(`/hardware/${ticket.hardware_gid}`)}
          />
          <TicketLocationItem
            locationGid={ticket.location_gid}
            locationName={ticket.location_name}
          />
        </>
      );
    }
    return (
      <div>
        {ticket.hardware_id ? (
          <PropertyListItemLink
            others={{ divider: true }}
            label={mode?.details.stationsTitle || ''}
            text={ticket.hardware_number}
            onClick={() => history.push(`/units/${ticket.hardware_id}`)}
          />
        ) : null}
        <TicketLocationItem
          locationGid={ticket.location_gid}
          locationName={ticket.location_name}
        />
        {ticket.module_id ? (
          <PropertyListItemLink
            others={{ divider: true }}
            label="Lock"
            text={ticket.module_id}
            onClick={() => history.push(`/units/locks/${ticket.module_id}`)}
          />
        ) : null}
      </div>
    );
  };

  const getHeader = (ticket: ITicket) => {
    let header = 'Ticket for ';
    // TODO: remove this
    if (system.new_hardware) {
      if (ticket?.hardware_number) {
        header += `${toTitleCase(ticket?.hardware_type_name)} ${ticket?.hardware_number}`;
      } else {
        header += ticket[ticket?.primary_identifier];
      }
    } else {
      header += `${mode?.details.unitsTitle || ''} ${ticket?.hardware_number}`;
    }
    return header;
  };

  const isActuallyLoading = Boolean(organizationInfo?.id)
    ? isLoading
    : isLoading || !systemLoaded;

  // Temporarily disabled: the safety-ticket rental block is not enforced yet,
  // so don't warn about it. Re-enable once the backend enforces the rule.
  // const warningMessage = useMemo(() => {
  //   if (ticket?.task_type === 'user_reported_damage') {
  //     return 'This hardware is unavailable for rental until the safety-critical ticket is resolved.';
  //   }
  // }, [ticket]);
  const warningMessage = undefined;

  const requiresMechanicRental =
    typeof system?.partner === 'number' &&
    MECHANIC_RENTAL_REQUIRED_PARTNERS.has(system.partner);

  const ticketResolved = Boolean(ticket?.completed_on || ticket?.canceled_on);

  const mechanicRentalReferenceTime = useMemo(() => {
    if (ticket?.completed_on) return new Date(ticket.completed_on);
    if (ticket?.canceled_on) return new Date(ticket.canceled_on);
    return new Date();
  }, [ticket?.completed_on, ticket?.canceled_on]);

  const { rentals: mechanicRentals, isLoading: mechanicRentalsLoading } =
    useMechanicRentalsLast24h(
      ticket?.hardware_gid || undefined,
      mechanicRentalReferenceTime,
      {
        enabled: requiresMechanicRental && Boolean(ticket?.hardware_gid),
      }
    );

  if (isActuallyLoading || !ticket?.id) {
    return <TicketDetailSkeleton />;
  }

  return (
    <PageContainer warningMessage={warningMessage}>
      <PageContent
        backLink={'/maintenance'}
        contentTitle={'Maintenance'}
        itemName={getHeader(ticket || ({} as ITicket))}
        itemChipValue={ticketId}
        detailIcon={<BuildIcon />}
        chipLabel={'Ticket ID'}
        tabsChildren={
          <Grid container spacing={2}>
            <Grid xs={12} lg={5}>
              <CardView
                title={'Ticket Details'}
                content={
                  <Stack>
                    <PropertyList>
                      <PropertyListItem
                        divider
                        label="Maintenance Type"
                        value={`${ticket.task_name}: ${ticket.task_description}`}
                      />
                      <PropertyListItem divider label="Target Date">
                        <SeverityPill color={pastDue ? 'error' : 'success'}>
                          {getLocaleDateString(ticket.due_on)}
                        </SeverityPill>
                      </PropertyListItem>
                      <PropertyListItem
                        divider
                        label="Created"
                        value={getLocaleDateTimeString(ticket.created_on)}
                      />
                      {ticket.completed_on ? (
                        <PropertyListItem
                          divider
                          label="Completed"
                          value={getLocaleDateTimeString(ticket.completed_on)}
                        />
                      ) : null}
                      <PropertyListItem
                        divider
                        label="Priority"
                        value={`${ticket.priority} - ${getPriorityName(ticket.priority)}`}
                      />
                      <PropertyListItem divider label="State">
                        <SeverityPill color={getStatusColor(ticket)}>
                          {getStatus(ticket)}
                        </SeverityPill>
                      </PropertyListItem>
                      {hardwareContent(ticket)}
                      {ticket.error_report_gid || ticket.error_report_id ? (
                        <PropertyListItemLink
                          others={{ divider: true }}
                          label="Original Problem Report"
                          text={ticket.error_report_gid || ticket.error_report_id}
                          onClick={() =>
                            history.push(
                              `/problems/${
                                ticket.error_report_gid || ticket.error_report_id
                              }`
                            )
                          }
                        />
                      ) : null}
                      {ticket.rental_gid ? (
                        <PropertyListItemLink
                          others={{ divider: true }}
                          label="Rental"
                          text={ticket.rental_gid}
                          onClick={() => history.push(`/rentals/${ticket.rental_gid}`)}
                        />
                      ) : null}
                      {ticket.completed_task ? (
                        <PropertyListItemLink
                          others={{ divider: true }}
                          label="Maintenance Log"
                          text={ticket.completed_task}
                          onClick={() =>
                            history.push(`/maintenance/log/${ticket.completed_task}`)
                          }
                        />
                      ) : null}
                      {ticket.assignee &&
                      admins.find((admin: IAdmin) => admin.email === ticket.assignee) ? (
                        <PropertyListItemLink
                          label="Assignee"
                          text={ticket.assignee_name}
                          onClick={() =>
                            history.push(`/settings/admin/${ticket.assignee}`)
                          }
                        />
                      ) : (
                        <PropertyListItem label="Assignee" value="Unassigned" />
                      )}
                      {ticket.reporter &&
                      admins.find((admin: IAdmin) => admin.email === ticket.reporter) ? (
                        <>
                          <Divider />
                          <PropertyListItemLink
                            others={{ divider: true }}
                            label="Reporter"
                            text={ticket.reporter_name}
                            onClick={() =>
                              history.push(`/settings/admin/${ticket.reporter}`)
                            }
                          />
                        </>
                      ) : null}

                      {organizationInfo?.id && (
                        <>
                          <ChangeOrganizationToSystem
                            system={ticket?.system_info || {}}
                          />
                          <Divider />
                        </>
                      )}
                      {system.partner === 1 || system.partner === 2 ? (
                        <>
                          <Divider />
                          <PropertyListItemLink
                            others={{ divider: true }}
                            label="Report Hardware Failure"
                            text="Contact Manufacturer"
                            onClick={() => getOEMContact()}
                          />
                        </>
                      ) : null}
                      <PropertyListItem divider label="Tags" dataId="tags">
                        <Grid container spacing={1}>
                          {tags.map(
                            // @ts-ignore
                            (tag) => (
                              <Grid key={tag.label}>
                                <Chip
                                  sx={{
                                    height: 'auto',
                                    py: 0.5,
                                    '& .MuiChip-label': {
                                      display: 'block',
                                      whiteSpace: 'normal',
                                    },
                                  }}
                                  onDelete={() => tag.onClick(tag.label)}
                                  label={tag.label}
                                />
                              </Grid>
                            )
                          )}
                          <Grid>
                            <IconButtonMenu
                              buttons={[
                                {
                                  label: '',
                                  startIcon: <AddIcon />,
                                  onClick: () => setAddTagModal(true),
                                },
                              ]}
                            />
                          </Grid>
                        </Grid>
                      </PropertyListItem>
                      {resolutionData?.reason && (
                        <>
                          <PropertyListItem
                            divider
                            label="Resolution Reason"
                            value={getResolutionReasonLabel(resolutionData.reason)}
                          />
                          {resolutionData.details && (
                            <PropertyListItem
                              divider
                              label="Resolution Details"
                              value={resolutionData.details}
                            />
                          )}
                          {resolutionData.resolved_by && (
                            <PropertyListItem
                              divider
                              label="Resolved By"
                              value={resolutionData.resolved_by}
                            />
                          )}
                        </>
                      )}
                    </PropertyList>
                  </Stack>
                }
              />
              {admin?.movaticAccess && ticketRaw?.usageProgress && (
                <Box mt={2}>
                  <CardView
                    title="Usage Progress"
                    content={
                      <Stack spacing={1}>
                        <UsageProgressBar
                          current={ticketRaw.usageProgress.current}
                          threshold={ticketRaw.usageProgress.threshold}
                        />
                        {ticketRaw.usageProgress.ticketCreatedOn && (
                          <small>
                            Since ticket created on{' '}
                            {new Date(
                              ticketRaw.usageProgress.ticketCreatedOn
                            ).toLocaleDateString()}
                          </small>
                        )}
                      </Stack>
                    }
                  />
                </Box>
              )}
              {requiresMechanicRental && (
                <Box mt={2}>
                  <MechanicRentalsList
                    rentals={mechanicRentals}
                    isLoading={mechanicRentalsLoading}
                    ticketResolved={ticketResolved}
                  />
                </Box>
              )}
            </Grid>
            <Grid xs={12} lg={7}>
              <Stack spacing={2}>
                {resolutionData?.location?.lat && resolutionData?.location?.lng && (
                  <CardView
                    title="Resolution Location"
                    content={
                      <Map
                        center={[
                          resolutionData.location.lat,
                          resolutionData.location.lng,
                        ]}
                        zoom={16}
                        maxZoom={19}
                        style={{ height: '20em' }}
                      >
                        <GenericMapTileLayer />
                        <Marker
                          position={[
                            resolutionData.location.lat,
                            resolutionData.location.lng,
                          ]}
                          icon={resolutionMarkerIcon}
                        />
                        {stations
                          .filter((s: { lat: number; lng: number }) => s.lat && s.lng)
                          .map(
                            (s: {
                              id: string;
                              lat: number;
                              lng: number;
                              type: string;
                            }) => (
                              <Marker
                                key={s.id}
                                position={[s.lat, s.lng]}
                                icon={mapStationMarker(s.type, s)}
                              />
                            )
                          )}
                      </Map>
                    }
                  />
                )}
                <TicketImageManager
                  ticketId={ticketRaw?.id || ''}
                  systemId={ticket?.system_info?.old_id || systemId}
                  existingImages={ticket?.images || []}
                  viewOnly={true}
                />
                <TicketTimeline
                  ticket={ticket}
                  communicationLog={ticketRaw?.communicationLog ?? null}
                  resolutionData={resolutionData}
                  admins={admins}
                  note={note}
                  onNoteChange={handleNoteChange}
                  onSaveNote={saveNote}
                  getResolutionReasonLabel={getResolutionReasonLabel}
                  isLoading={isActuallyLoading}
                />
              </Stack>
            </Grid>
          </Grid>
        }
        itemTopButtons={
          !ticket?.completed_on && !ticket?.canceled_on ? (
            <IconButtonMenu
              buttons={[
                {
                  label: 'Cancel',
                  onClick: () => toggleConfirmationModal(),
                },
                {
                  label: 'Edit',
                  onClick: () => history.push(`/maintenance/edit/${ticketId}`),
                  startIcon: <EditIcon />,
                },
                {
                  label: 'Close',
                  onClick: () => closeTicket(),
                  startIcon: <CloseIcon />,
                },
              ]}
            />
          ) : undefined
        }
      />
      <MovaticCustomModal
        open={showModal}
        onClose={toggleConfirmationModal}
        isLoading={isCanceling}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: isSmallScreen ? '90vw' : '50vw',
            minWidth: isSmallScreen ? '90vw' : '500px',
            margin: isSmallScreen ? 1 : 'auto',
            width: '100%',
          },
        }}
        content={
          <Box>
            <MovaticFieldGroupSelect
              label="Cancellation Reason"
              description="Why is this ticket being cancelled?"
              optionNodes={CANCEL_RESOLUTION_REASONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
              value={cancelReason}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setCancelReason(e.target.value)
              }
              name="cancelReason"
            />
            <MovaticFieldGroupText
              multiline
              maxRows={4}
              minRows={1}
              label="Cancellation Details"
              placeholder="Additional details about the cancellation"
              description="(Optional)"
              name="cancelDetails"
              value={cancelDetails}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setCancelDetails(e.target.value)
              }
            />
            <TicketImageManager
              ref={cancelImageManagerRef}
              ticketId={ticketRaw?.id || ''}
              systemId={ticket?.system_info?.old_id || systemId}
              existingImages={ticket?.images || []}
              readOnly={false}
            />
          </Box>
        }
        title="Cancel Maintenance Ticket"
        action={
          <IconButtonMenu
            buttons={[
              { label: 'Back', onClick: toggleConfirmationModal },
              {
                label: 'Cancel Ticket',
                onClick: handleCancelTicket,
                disabled: isCanceling,
              },
            ]}
          />
        }
      />
      <MovaticCustomModal
        open={addTagModal}
        onClose={() => setAddTagModal(false)}
        content={
          <MovaticFieldGroupSelectFilter
            label={'Tags'}
            isLoading={isTagLoading}
            value={value}
            onChange={(event, newValue) => {
              setValue(
                // @ts-ignore
                newValue.filter((option) =>
                  // @ts-ignore
                  loadedTags.some((item) => item.label === option.label)
                )
              );
            }}
            options={loadedTags}
          />
        }
        title="Add Tag"
        action={
          <IconButtonMenu
            buttons={[
              { label: 'Cancel', onClick: () => setAddTagModal(false) },
              {
                label: 'Save',
                disabled: loadedTags?.length === 0,
                onClick: () => {
                  CustomAsyncToast({
                    promise: () =>
                      addTagsToMaintenanceTicket(
                        ticket.old_id,
                        {
                          tags: [
                            ...(ticket?.tags || []),
                            // @ts-ignore
                            ...value.map((item) => item.label),
                          ],
                        },
                        ticket?.system_info?.old_id
                      ),
                    successMessage: () => 'Tags added successfully',
                    loadingMessage: 'Adding tags...',
                    errorMessage:
                      'Oops! Looks like there was an error while adding the tags. Please try again later.',
                  }).then((results) => {
                    if (results) {
                      setAddTagModal(false);
                      refetch();
                      setValue([]);
                    }
                  });
                },
              },
            ]}
          />
        }
      />
      <MovaticCustomModal
        customStyles={{
          content: {
            minWidth: 'none',
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: isSmallScreen ? '90vw' : '30vw',
            margin: isSmallScreen ? 1 : 'auto',
            width: '100%',
          },
        }}
        open={deleteTagModal}
        onClose={() => setDeleteTagModal(false)}
        content={`Are you sure you want to remove the tag ${tagToDelete}?`}
        title={'Remove Tag'}
        action={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => setDeleteTagModal(false),
              },
              {
                label: 'Submit',
                onClick: () => {
                  CustomAsyncToast({
                    promise: () =>
                      addTagsToMaintenanceTicket(ticket.id, {
                        tags: ticket.tags
                          ? ticket.tags.filter((item: string) => item !== tagToDelete)
                          : [],
                      }),
                    successMessage: () => 'Tag removed successfully',
                    loadingMessage: 'Removing tag...',
                    errorMessage:
                      'Oops! Looks like there was an error while adding the tag to the Ticket. Please try again later.',
                  }).then((results) => {
                    if (results) {
                      setDeleteTagModal(false);
                      refetch();
                      setTagToDelete('');
                    }
                  });
                },
              },
            ]}
          />
        }
      />
    </PageContainer>
  );
};

export default connect(
  (state: {
    admin: { admin: { admin: IAdmin }; systemAdmins: IAdmin[] };
    system: { current: ISystem; isLoaded: boolean };
    mode: IMode;
  }) => ({
    admin: state.admin.admin.admin,
    admins: state.admin.systemAdmins,
    system: state.system.current,
    systemLoaded: state.system.isLoaded,
    mode: state.mode,
  }),
  () => ({})
)(TicketDetail);
